/**
 * Atlas — Dashboard page. Renders role-appropriate view.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProperties } from '../hooks/useData';
import { propertyAPI } from '../services/api';
import { Panel, GradePill, Spinner, EmptyState } from '../components/common/UIComponents';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function fmtAddress(p) {
  return `${p.city} ${p.state} ${p.zip_code}`;
}

const OCCUPANCY_CFG = {
  owner_occupied: { label: 'Owner Occupied', bg: 'rgba(31,58,46,0.1)',   color: 'var(--forest)' },
  rented:         { label: 'Rented',         bg: 'rgba(37,99,235,0.1)',  color: '#1d4ed8' },
  vacant:         { label: 'Vacant',         bg: 'rgba(220,38,38,0.1)', color: '#dc2626' },
  for_lease:      { label: 'For Lease',      bg: 'rgba(234,179,8,0.1)', color: '#b45309' },
  for_sale:       { label: 'For Sale',       bg: 'rgba(147,51,234,0.1)', color: '#7c3aed' },
};

function OccupancyBadge({ status }) {
  const cfg = OCCUPANCY_CFG[status];
  if (!cfg) return null;
  return (
    <span style={{
      fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33`,
    }}>{cfg.label}</span>
  );
}

function PropertyCard({ property, onClick, footer }) {
  const p = property;
  const topFeatures = (p.features || []).slice(0, 5);

  return (
    <div className="panel lift" onClick={onClick}
      style={{ cursor: 'pointer', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

      {/* Cover image */}
      {p.cover_image_url ? (
        <img
          src={`${API_BASE}${p.cover_image_url}`}
          alt="Property"
          style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block', flexShrink: 0 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{
          width: '100%', height: 100, flexShrink: 0,
          background: 'linear-gradient(135deg, rgba(31,58,46,0.08) 0%, rgba(31,58,46,0.04) 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'rgba(31,58,46,0.2)', fontSize: 36,
        }}>🏠</div>
      )}

      {/* Card body */}
      <div style={{ padding: '14px 16px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* Type + status row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {p.condition_grade && <GradePill grade={p.condition_grade} />}
          {p.occupancy_status && <OccupancyBadge status={p.occupancy_status} />}
          {p.style && (
            <span style={{ fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {p.style}
            </span>
          )}
        </div>

        {/* Address */}
        <div>
          <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)', lineHeight: 1.2 }}>
            {p.address_line1}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>{fmtAddress(p)}</div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {p.bedrooms != null && <Stat label="Beds" value={p.bedrooms} />}
          {p.bathrooms != null && <Stat label="Baths" value={p.bathrooms} />}
          {p.car_parks != null ? <Stat label="Car Parks" value={p.car_parks} /> : null}
          {p.built_up_area ? <Stat label="Built Up (sqm)" value={p.built_up_area.toLocaleString()} /> : null}
          {p.year_built ? <Stat label="Built" value={p.year_built} /> : null}
        </div>

        {/* Feature tags */}
        {topFeatures.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 2 }}>
            {topFeatures.map((feat) => (
              <span key={feat} style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                background: 'rgba(31,58,46,0.07)', color: 'var(--forest)',
                border: '1px solid rgba(31,58,46,0.13)',
              }}>{feat}</span>
            ))}
            {(p.features || []).length > 5 && (
              <span style={{ fontSize: 10, color: 'var(--ink-4)', padding: '2px 4px' }}>
                +{p.features.length - 5} more
              </span>
            )}
          </div>
        )}

        {footer && <div style={{ marginTop: 'auto', paddingTop: 4 }}>{footer}</div>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();

  switch (user?.role) {
    case 'owner': return <OwnerDashboard />;
    case 'agent': return <AgentDashboard />;
    case 'buyer': return <BuyerDashboard />;
    case 'pro':   return <ProDashboard />;
    default:      return <OwnerDashboard />;
  }
}

function OwnerDashboard() {
  const { items: properties, loading, refetch } = useProperties();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);

  if (loading) return <Spinner />;

  return (
    <div className="page-wrap">
      <div className="page-header">
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            My Properties
          </div>
          <h1 className="serif" style={{ fontSize: 32, margin: '4px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
            Property Overview
          </h1>
        </div>
        <button className="btn primary" style={{ flexShrink: 0 }} onClick={() => setShowAddModal(true)}>
          + Add property
        </button>
      </div>

      {properties.length === 0 ? (
        <EmptyState message="No properties yet. Add your first property to start building its history." />
      ) : (
        <div className="grid-cards">
          {properties.map((p) => (
            <PropertyCard key={p.id} property={p} onClick={() => navigate(`/properties/${p.id}`)} />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddPropertyModal
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); refetch(); }}
        />
      )}
    </div>
  );
}

const PERM_META = [
  { key: 'can_manage_tenancy', label: 'Tenancy' },
  { key: 'can_manage_maintenance', label: 'Maintenance' },
  { key: 'can_upload_documents', label: 'Documents' },
  { key: 'can_book_professionals', label: 'Book Pros' },
  { key: 'can_view_financials', label: 'Financials' },
];

function AgentPropertyCard({ property, navigate }) {
  const [access, setAccess] = useState(null);

  useEffect(() => {
    propertyAPI.getMyAccess(property.id)
      .then(({ data }) => setAccess(data))
      .catch(() => {});
  }, [property.id]);

  const footer = access ? (
    <div>
      {(access.role_label || access.access_level) && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {access.role_label && (
            <span style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
              background: 'rgba(31,58,46,0.1)', color: 'var(--forest)',
            }}>{access.role_label}</span>
          )}
          {access.access_level && (
            <span style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
              background: 'rgba(0,0,0,0.06)', color: 'var(--ink-3)',
            }}>{access.access_level}</span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {PERM_META.map(({ key, label }) => (
          <span key={key} style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 10,
            background: access[key] ? 'rgba(21,128,61,0.1)' : 'rgba(0,0,0,0.05)',
            color: access[key] ? '#15803d' : 'var(--ink-4)',
            border: `1px solid ${access[key] ? 'rgba(21,128,61,0.2)' : 'transparent'}`,
          }}>
            {access[key] ? '✓' : '–'} {label}
          </span>
        ))}
      </div>
      {access.notes && (
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6, fontStyle: 'italic' }}>
          {access.notes}
        </div>
      )}
    </div>
  ) : null;

  return (
    <PropertyCard
      property={property}
      onClick={() => navigate(`/properties/${property.id}`)}
      footer={footer}
    />
  );
}

function AgentDashboard() {
  const { items: properties, loading } = useProperties();
  const navigate = useNavigate();
  if (loading) return <Spinner />;

  return (
    <div className="page-wrap">
      <h1 className="serif" style={{ fontSize: 32, color: 'var(--forest)', fontWeight: 600, marginBottom: 6 }}>
        Properties Assigned to You
      </h1>
      <div style={{ fontSize: 13, color: 'var(--ink-4)', marginBottom: 22 }}>
        Your permissions for each property are shown below.
      </div>
      {properties.length === 0 ? (
        <EmptyState message="No properties shared with you yet. Ask a property owner to grant you access." />
      ) : (
        <div className="grid-cards">
          {properties.map((p) => (
            <AgentPropertyCard key={p.id} property={p} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function BuyerDashboard() {
  const { items: properties, loading } = useProperties();
  const navigate = useNavigate();
  if (loading) return <Spinner />;

  return (
    <div className="page-wrap">
      <h1 className="serif" style={{ fontSize: 32, color: 'var(--forest)', fontWeight: 600, marginBottom: 22 }}>
        Properties Shared with You
      </h1>
      {properties.length === 0 ? (
        <EmptyState message="No properties have been shared with you yet." />
      ) : (
        <div className="grid-cards">
          {properties.map((p) => (
            <PropertyCard key={p.id} property={p} onClick={() => navigate(`/properties/${p.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProDashboard() {
  return (
    <div className="page-wrap">
      <h1 className="serif" style={{ fontSize: 32, color: 'var(--forest)', fontWeight: 600, marginBottom: 22 }}>
        Pro Dashboard
      </h1>
      <div className="grid-4" style={{ marginBottom: 22 }}>
        {[
          { label: 'New Leads', val: '—', sub: 'Check bookings' },
          { label: 'Active Jobs', val: '—', sub: 'In progress' },
          { label: 'Avg Rating', val: '—', sub: 'From reviews' },
          { label: 'PropAtlas Score', val: '—', sub: 'Build your profile' },
        ].map((s, i) => (
          <Panel key={i} style={{ padding: 18 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>{s.label}</div>
            <div className="serif" style={{ fontSize: 30, fontWeight: 600, color: 'var(--forest)', marginTop: 4 }}>{s.val}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{s.sub}</div>
          </Panel>
        ))}
      </div>
    </div>
  );
}

const AU_STATES = [
  { value: 'NSW', label: 'NSW — New South Wales' },
  { value: 'VIC', label: 'VIC — Victoria' },
  { value: 'QLD', label: 'QLD — Queensland' },
  { value: 'WA',  label: 'WA — Western Australia' },
  { value: 'SA',  label: 'SA — South Australia' },
  { value: 'TAS', label: 'TAS — Tasmania' },
  { value: 'NT',  label: 'NT — Northern Territory' },
  { value: 'ACT', label: 'ACT — Australian Capital Territory' },
];

const PROPERTY_TYPES = [
  { value: 'single_family', label: 'House' },
  { value: 'condo',         label: 'Apartment / Unit' },
  { value: 'townhouse',     label: 'Townhouse' },
  { value: 'multi_family',  label: 'Multi-Family' },
  { value: 'land',          label: 'Land' },
];

const SELECT_STYLE = {
  height: 36, padding: '0 10px', border: '1px solid var(--hairline)',
  borderRadius: 4, background: 'var(--card)', fontSize: 13, width: '100%',
};

const AU_STATE_MAP = {
  'New South Wales': 'NSW',
  'Victoria': 'VIC',
  'Queensland': 'QLD',
  'Western Australia': 'WA',
  'South Australia': 'SA',
  'Tasmania': 'TAS',
  'Northern Territory': 'NT',
  'Australian Capital Territory': 'ACT',
};

function AddressAutocomplete({ onSelect }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const timer = useRef(null);
  const listRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 4) { setSuggestions([]); setOpen(false); return; }
    setFetching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&countrycodes=au&limit=6`,
        { headers: { 'Accept-Language': 'en-AU' } }
      );
      const data = await res.json();
      setSuggestions(data);
      setActive(-1);
      setOpen(data.length > 0);
    } catch { /* ignore network errors */ }
    finally { setFetching(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(val), 350);
  };

  const handleSelect = (item) => {
    const a = item.address || {};
    const houseNo = a.house_number || '';
    const road = a.road || a.pedestrian || a.footway || a.cycleway || '';
    const address_line1 = [houseNo, road].filter(Boolean).join(' ');
    const city = a.suburb || a.town || a.village || a.hamlet || a.city_district || a.city || a.county || '';
    const state = AU_STATE_MAP[a.state] || a.state || '';
    const zip_code = a.postcode || '';

    onSelect({ address_line1, city, state, zip_code });
    setQuery(address_line1 || item.display_name.split(',')[0].trim());
    setSuggestions([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); handleSelect(suggestions[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="field" style={{ position: 'relative' }}>
      <label>Search address</label>
      <div style={{ position: 'relative' }}>
        <input
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Start typing a street address…"
          autoComplete="off"
          style={{ paddingRight: 32 }}
        />
        {fetching && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--ink-4)' }}>
            ↻
          </span>
        )}
      </div>
      {open && (
        <div ref={listRef} style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 8,
          zIndex: 200, maxHeight: 240, overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }}>
          {suggestions.map((item, i) => {
            const parts = item.display_name.split(',');
            const main = parts.slice(0, 2).join(',').trim();
            const sub = parts.slice(2, 4).join(',').trim();
            return (
              <div
                key={item.place_id}
                onMouseDown={() => handleSelect(item)}
                style={{
                  padding: '10px 14px', cursor: 'pointer',
                  borderBottom: i < suggestions.length - 1 ? '1px solid var(--hairline)' : 'none',
                  background: active === i ? 'rgba(31,58,46,0.06)' : 'transparent',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={() => setActive(i)}
              >
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{main}</div>
                {sub && <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{sub}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddPropertyModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    property_type: 'single_family',
    style: '',
    year_built: '',
    bedrooms: '',
    bathrooms: '',
    car_parks: '',
    lot_size: '',
    built_up_area: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await propertyAPI.create({
        ...form,
        country: 'AU',
        year_built:    form.year_built    ? parseInt(form.year_built, 10)    : null,
        bedrooms:      form.bedrooms      ? parseInt(form.bedrooms, 10)      : null,
        bathrooms:     form.bathrooms     ? parseFloat(form.bathrooms)       : null,
        car_parks:     form.car_parks      ? parseInt(form.car_parks, 10)      : null,
        built_up_area: form.built_up_area ? parseInt(form.built_up_area, 10) : null,
        address_line2: form.address_line2 || null,
        style:         form.style         || null,
        lot_size:      form.lot_size       || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save property');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
              New property
            </div>
            <h2 className="serif" style={{ fontSize: 22, margin: '2px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
              Add Property
            </h2>
          </div>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
            {error && <div style={{ color: 'var(--crimson)', fontSize: 13 }}>{error}</div>}

            {/* Address autocomplete */}
            <AddressAutocomplete
              onSelect={({ address_line1, city, state, zip_code }) =>
                setForm(f => ({ ...f, address_line1, city, state, zip_code }))
              }
            />

            {/* Street address — pre-filled by autocomplete, editable */}
            <div className="field">
              <label>Street Address</label>
              <input
                value={form.address_line1}
                onChange={update('address_line1')}
                required
                placeholder="e.g. 12 Harbour View Terrace"
              />
            </div>

            <div className="field">
              <label>Unit / Apartment (optional)</label>
              <input
                value={form.address_line2}
                onChange={update('address_line2')}
                placeholder="e.g. Unit 4"
              />
            </div>

            {/* Suburb / State / Postcode */}
            <div className="r-cols-addr" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
              <div className="field">
                <label>Suburb / City</label>
                <input
                  value={form.city}
                  onChange={update('city')}
                  required
                  placeholder="e.g. Mosman"
                />
              </div>

              <div className="field">
                <label>State / Territory</label>
                <select value={form.state} onChange={update('state')} required style={SELECT_STYLE}>
                  <option value="">Select…</option>
                  {AU_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              <div className="field">
                <label>Postcode</label>
                <input
                  value={form.zip_code}
                  onChange={update('zip_code')}
                  required
                  placeholder="2088"
                  maxLength={4}
                  inputMode="numeric"
                />
              </div>
            </div>

            {/* Property type + style */}
            <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="field">
                <label>Property Type</label>
                <select value={form.property_type} onChange={update('property_type')} style={SELECT_STYLE}>
                  {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Style (optional)</label>
                <input
                  value={form.style}
                  onChange={update('style')}
                  placeholder="e.g. Federation, Queenslander"
                />
              </div>
            </div>

            {/* Numeric details */}
            <div className="r-cols-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              <div className="field">
                <label>Year Built</label>
                <input type="number" value={form.year_built} onChange={update('year_built')} placeholder="1985" min={1800} max={new Date().getFullYear()} />
              </div>
              <div className="field">
                <label>Bedrooms</label>
                <input type="number" value={form.bedrooms} onChange={update('bedrooms')} placeholder="3" min={0} step={1} />
              </div>
              <div className="field">
                <label>Bathrooms</label>
                <input type="number" value={form.bathrooms} onChange={update('bathrooms')} placeholder="2" min={0} step={0.5} />
              </div>
              <div className="field">
                <label>Car Parks</label>
                <input type="number" value={form.car_parks} onChange={update('car_parks')} placeholder="2" min={0} max={99} step={1} />
              </div>
            </div>

            <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="field">
                <label>Built Up Area (sqm)</label>
                <input type="number" value={form.built_up_area} onChange={update('built_up_area')} placeholder="e.g. 320" min={0} step={1} />
              </div>
              <div className="field">
                <label>Land / Lot Size (optional)</label>
                <input
                  value={form.lot_size}
                  onChange={update('lot_size')}
                  placeholder="e.g. 556 sqm"
                />
              </div>
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? 'Saving…' : '✓ Add property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>{label}</div>
      <div className="serif tnum" style={{ fontSize: 18, fontWeight: 600, marginTop: 1 }}>{value}</div>
    </div>
  );
}
